/* istanbul ignore file */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { sendLoyaltyModalEvent } from 'common/util/gtm-helper';
import Modal from 'common/components/modal-full';
import defaultStyles from 'commonStyles/components/coupon/_loyalty-simplified';
import styleOverrides from 'styles/components/coupon/_loyalty-simplified';
import useStyleOverrides from 'common/components/hooks/use-style-overrides';
import { translate } from 'common/localization';
import BcsCloseIcon from 'bcs/icons/close';
import ExpeditionPerksIcon from 'common/icons/loyalty/expedition-perks-logo';
import LoyaltyCouponRestrictions from './loyalty-coupon-restrictions';
import LoyaltyCouponSimplifiedNewUser from './loyalty-coupon-simplified-newuser';
import LoyaltyCouponSimplifiedMember from './loyalty-coupon-simplified-member';
import LoyaltyCouponSimplifiedRecognized from './loyalty-coupon-simplified-recognized';
import LoyaltyCouponSimplifiedSuccess from './loyalty-coupon-simplified-success';

const LoyaltyCouponSimplifiedModal = (props) => {
  const {
    restrictions,
    couponId,
    images,
    headerTitles,
    closeBanner,
    disclaimers,
    bodyText,
    cta,
    isLoading,
    error,
    claimCoupon,
    isCouponApplied,
    profile,
  } = props;
  const styles = useStyleOverrides(defaultStyles, styleOverrides);
  const [isOpen, setIsOpen] = useState(true);
  const [isRestrictionOpen, setRestrictionOpen] = useState(false);
  const [isLoyaltyMember, setLoyaltyMember] = useState(BC.user.loyalty.isUserLoyaltyRegistered);
  const [isExplicitOrAutoLoggedIn, setIsExplicitOrAutoLoggedIn] = useState(
    BC.user.isExplicitOrAutoLoggedIn
  );
  const [textKey, setTextKey] = useState('notRecognized');

  const getEventLabel = () => {
    switch (true) {
      case isCouponApplied:
        return 'success';
      case !isExplicitOrAutoLoggedIn:
        return 'unrecognized';
      case !isLoyaltyMember:
        return 'nonmember';
      default:
        return 'existing member';
    }
  };

  const onExitModal = () => {
    setIsOpen(false);
    sendLoyaltyModalEvent('dismiss modal', getEventLabel());
    closeBanner();
  };

  const openExclusionsModal = () => {
    setRestrictionOpen(true);
  };

  useEffect(() => {
    if (BC.user.isExplicitOrAutoLoggedIn && BC.user.isLoggedIn && BC.profile.id) {
      if (BC.user.loyalty.isUserLoyaltyRegistered) {
        sendLoyaltyModalEvent('impression', 'existing member');
      } else {
        sendLoyaltyModalEvent('impression', 'nonmember');
      }
    } else {
      sendLoyaltyModalEvent('impression', 'unrecognized');
    }
  }, [isLoyaltyMember]);

  useEffect(() => {
    setIsExplicitOrAutoLoggedIn(BC.user.isExplicitOrAutoLoggedIn);
    if (BC.user.isLoggedIn && BC.profile.id) {
      setLoyaltyMember(BC.user.loyalty.isUserLoyaltyRegistered);
    }
  }, []);

  useEffect(() => {
    const handleLoyaltyUpdated = () => {
      setLoyaltyMember(BC.user.loyalty.isUserLoyaltyRegistered);
    };

    BC.subscribe('loyalty.user.updated', handleLoyaltyUpdated);

    return () => {
      BC.unsubscribe('loyalty.user.updated', handleLoyaltyUpdated);
    };
  }, []);

  useEffect(() => {
    if (isCouponApplied) {
      return setTextKey('success');
    }
    if (isLoyaltyMember) {
      return setTextKey('member');
    }

    if (isExplicitOrAutoLoggedIn && !isLoyaltyMember) {
      return setTextKey('recognized');
    }

    return setTextKey('notRecognized');
  });

  const contentRender = () => {
    if (isCouponApplied) {
      return (
        <LoyaltyCouponSimplifiedSuccess
          couponId={couponId}
          bodyText={bodyText}
          disclaimers={disclaimers}
          cta={cta}
          handleContinue={closeBanner}
        />
      );
    } else if (isExplicitOrAutoLoggedIn && isLoyaltyMember) {
      return (
        <LoyaltyCouponSimplifiedMember
          couponId={couponId}
          disclaimers={disclaimers}
          bodyText={bodyText}
          claimCoupon={claimCoupon}
          isLoading={isLoading}
          error={error}
        />
      );
    } else if (isExplicitOrAutoLoggedIn && !isLoyaltyMember) {
      return (
        <LoyaltyCouponSimplifiedRecognized
          couponId={couponId}
          disclaimers={disclaimers}
          claimCoupon={claimCoupon}
          profile={profile}
          error={error}
        />
      );
    }

    return (
      <LoyaltyCouponSimplifiedNewUser
        couponId={couponId}
        claimCoupon={claimCoupon}
        isLoading={isLoading}
        error={error}
        disclaimers={disclaimers}
        onSignInClick={closeBanner}
      />
    );
  };

  return (
    <>
      <Modal
        id="loyaltyBundledCouponBanner"
        modalTitle={couponId}
        showModal={isOpen}
        onExitFn={onExitModal}
        onBackgroundClick={onExitModal}
        onEscapeKey={onExitModal}
        modalContentClass={styles.modalContentClass}
        firstFocusElement="#close-loyalty-simplified"
      >
        <button
          className={`btn-reset ${styles.closeBtn}`}
          id="close-loyalty-simplified"
          onClick={onExitModal}
        >
          <BcsCloseIcon />
          <span className="visually-hidden">
            {translate('label.coupon.banner.close')}
          </span>
        </button>
        <div className={styles.contentWrap}>
          <div
            className={`${styles.contentSection} ${styles.topBanner}`}
            style={{ backgroundImage: `url(${images.background.url})` }}
          >
            <img
              className={styles.bannerImg}
              src={images.promoImageLight.url}
            />
            <p className={styles.bannerText}>
              <button
                className={`btn-reset ${styles.bannerLink}`}
                onClick={openExclusionsModal}
              >
                {restrictions.cta}
              </button>
            </p>
          </div>
          <div className={`${styles.contentSection} ${styles.bannerContent}`}>
            <ExpeditionPerksIcon
              className={`${styles.perksLogo} ${styles.perksLogoMargin}`}
            />
            <p className={styles.modalCtaTitle}>
              {headerTitles ? headerTitles[textKey] : null}
            </p>
            {contentRender()}
          </div>
        </div>
      </Modal>

      <LoyaltyCouponRestrictions
        restrictions={restrictions}
        isOpen={isRestrictionOpen}
        onExitModal={() => {
          setRestrictionOpen(false);
        }}
      />
    </>
  );
};

LoyaltyCouponSimplifiedModal.propTypes = {
  restrictions: PropTypes.shape({}),
  couponId: PropTypes.string,
  images: PropTypes.shape({}),
  closeBanner: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.shape({}),
  disclaimers: PropTypes.shape({}),
  profile: PropTypes.shape({}),
};

LoyaltyCouponSimplifiedModal.defaultProps = {
  isCouponApplied: false,
};

export default LoyaltyCouponSimplifiedModal;
